import { useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { ChevronRight, Plus } from 'lucide-react'
import {
  SHOPPING_SECTIONS,
  SHOPPING_SECTION_LABELS,
  UNITS,
  UNIT_LABELS,
  type ShoppingSection,
  type Unit,
} from '@shared/events'
import { shoppingList, type ShoppingLine } from '@shared/shopping'
import { addDays, startOfWeek } from '@shared/time'
import { Hoja } from '../components/Hoja'
import { Boton, Cabecera, Campo, Casilla, Vacio } from '../components/ui'
import { useEvents, useToday } from '../lib/hooks'
import { useIrA } from '../lib/navegacion'
import { useSession } from '../lib/session'
import { newEvent, saveEvent } from '../lib/sync'

/**
 * La lista sale del plan y no se guarda: lo que se guarda es lo que se añade
 * a mano y lo que se va tachando. Tacharlo se sincroniza, así que en el súper
 * cada uno ve lo que ha cogido el otro en cuanto hay cobertura.
 */
export function Compra() {
  const events = useEvents()
  const today = useToday()
  const { user } = useSession()
  const irA = useIrA()
  const [offset, setOffset] = useState(0)
  const [anadiendo, setAnadiendo] = useState(false)

  const weekKey = useMemo(() => addDays(startOfWeek(today), offset * 7), [today, offset])
  const list = useMemo(() => shoppingList(weekKey, events), [weekKey, events])
  const progreso = list.total > 0 ? list.bought / list.total : 0

  async function alternar(line: ShoppingLine): Promise<void> {
    if (!user) return
    await saveEvent(
      newEvent({
        type: 'shopping_check',
        createdBy: user.id,
        payload: { weekKey, itemKey: line.itemKey, bought: !line.bought },
      }),
    )
  }

  return (
    <>
      <Cabecera titulo="Compra" subtitulo={`${list.bought} de ${list.total} cogidos`}>
        <button
          type="button"
          onClick={() => setOffset((value) => (value === 0 ? 1 : 0))}
          className="label-caps inline-flex min-h-11 items-center gap-1 rounded-full border border-border bg-card px-3.5 text-muted-foreground outline-none transition-colors hover:border-primary/40 hover:text-primary focus-visible:ring-2 focus-visible:ring-ring"
        >
          {offset === 0 ? 'La que viene' : 'Esta semana'}
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </Cabecera>

      <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-surface" aria-hidden="true">
        <motion.div
          className="h-full rounded-full bg-sage"
          initial={false}
          animate={{ width: `${progreso * 100}%` }}
          transition={{ type: 'spring', stiffness: 160, damping: 26 }}
        />
      </div>

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={weekKey}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -24 }}
          transition={{ duration: 0.24, ease: 'easeOut' }}
        >
          {list.total === 0 ? (
            <div className="mt-5">
              <Vacio>
                Nada que comprar: planifica la semana y la lista sale sola de las recetas.
                <button
                  type="button"
                  onClick={() => irA('platos')}
                  className="mt-3 block w-full text-sm font-medium text-primary hover:underline"
                >
                  Planificar la semana
                </button>
              </Vacio>
            </div>
          ) : (
            list.groups.map((group, index) => (
              <motion.section
                key={group.section}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05, type: 'spring', stiffness: 320, damping: 30 }}
                className="mt-5"
              >
                <h2 className="label-caps mb-2 ml-1 font-sans text-muted-foreground">
                  {SHOPPING_SECTION_LABELS[group.section]}
                </h2>
                <ul className="divide-y divide-border/70 rounded-3xl border border-border/70 bg-card px-2">
                  {group.lines.map((line) => (
                    <li key={line.itemKey} className="flex items-center gap-1 py-1">
                      <Casilla
                        marcada={line.bought}
                        label={`${line.bought ? 'Desmarcar' : 'Marcar'} ${line.name}`}
                        onClick={() => void alternar(line)}
                      />
                      <div className="min-w-0 flex-1 py-1.5 pr-2">
                        <p
                          className={`truncate font-medium transition-colors duration-300 ${
                            line.bought ? 'text-muted-foreground line-through decoration-sage/70' : ''
                          }`}
                        >
                          {line.name}
                          {line.quantity !== null && (
                            <span className="ml-1.5 text-sm font-normal text-muted-foreground">
                              {line.hasUnknownQuantity ? '+ de ' : ''}
                              {formatQuantity(line.quantity)}
                              {line.unit ? ` ${UNIT_LABELS[line.unit]}` : ''}
                            </span>
                          )}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">{procedencia(line)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </motion.section>
            ))
          )}
        </motion.div>
      </AnimatePresence>

      <Boton variante="principal" ancho className="mt-5" onClick={() => setAnadiendo(true)}>
        <Plus className="h-4 w-4" strokeWidth={2.4} />
        Añadir algo más
      </Boton>

      <AnimatePresence>
        {anadiendo && <FormularioExtra key="extra" weekKey={weekKey} onHecho={() => setAnadiendo(false)} />}
      </AnimatePresence>
    </>
  )
}

function procedencia(line: ShoppingLine): string {
  const partes: string[] = []
  if (line.recipes.length > 0) partes.push(line.recipes.join(' · '))
  if (line.manual && line.recipes.length === 0) partes.push('añadido a mano')
  if (line.splitByUnit) partes.push('también en otra unidad')
  return partes.join(' — ')
}

function formatQuantity(quantity: number): string {
  return Number.isInteger(quantity) ? String(quantity) : quantity.toFixed(1).replace('.', ',')
}

function FormularioExtra({ weekKey, onHecho }: { weekKey: string; onHecho: () => void }) {
  const { user } = useSession()
  const [name, setName] = useState('')
  const [quantity, setQuantity] = useState('')
  const [unit, setUnit] = useState<Unit | ''>('')
  const [section, setSection] = useState<ShoppingSection>('otros')

  async function guardar(): Promise<void> {
    if (!user || !name.trim()) return
    const cantidad = quantity.trim() ? Number(quantity.replace(',', '.')) : null
    await saveEvent(
      newEvent({
        type: 'shopping_extra',
        createdBy: user.id,
        payload: {
          weekKey,
          name: name.trim(),
          quantity: cantidad !== null && Number.isFinite(cantidad) ? cantidad : null,
          unit: unit || null,
          section,
        },
      }),
    )
    onHecho()
  }

  return (
    <Hoja titulo="Añadir a la compra" onCerrar={onHecho}>
      <Campo id="extra-nombre" label="Qué">
        <input
          id="extra-nombre"
          className="campo"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Papel higiénico"
          autoFocus
        />
      </Campo>

      <div className="grid grid-cols-[1fr_5.5rem_5.5rem] gap-2">
        <Campo id="extra-seccion" label="Sección">
          <select
            id="extra-seccion"
            className="campo"
            value={section}
            onChange={(event) => setSection(event.target.value as ShoppingSection)}
          >
            {SHOPPING_SECTIONS.map((value) => (
              <option key={value} value={value}>
                {SHOPPING_SECTION_LABELS[value]}
              </option>
            ))}
          </select>
        </Campo>
        <Campo id="extra-cantidad" label="Cantidad">
          <input
            id="extra-cantidad"
            className="campo"
            inputMode="decimal"
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
          />
        </Campo>
        <Campo id="extra-unidad" label="Unidad">
          <select
            id="extra-unidad"
            className="campo px-2"
            value={unit}
            onChange={(event) => setUnit(event.target.value as Unit | '')}
          >
            <option value="">—</option>
            {UNITS.map((value) => (
              <option key={value} value={value}>
                {UNIT_LABELS[value]}
              </option>
            ))}
          </select>
        </Campo>
      </div>

      <Boton variante="principal" ancho className="mt-2" disabled={!name.trim()} onClick={guardar}>
        Añadir
      </Boton>
    </Hoja>
  )
}
